import { useLayoutEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'

/** True when `calc(1vw / 1px)` typed length÷length works (Chrome, Safari, …). */
function cssTypedLengthRatioSupported(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    CSS.supports !== undefined &&
    CSS.supports('width', 'calc(1vw / 1px)')
  )
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

function readViewportSize(): { innerH: number; vh: number; vw: number } {
  const vv = window.visualViewport
  const vw = vv?.width ?? window.innerWidth
  const vh = vv?.height ?? window.innerHeight
  return { vw, vh, innerH: window.innerHeight }
}

/** Matches former :root clamp(0.7, min(100vw/420, 100dvh/820), 1.5) using typed px math. */
export function computeRootMantineScale(vw: number, vh: number): number {
  return clamp(Math.min(vw / 420, vh / 820), 0.7, 1.5)
}

/**
 * Matches former .game-route-shell formula + (max-height: 649px) → 0.8.
 * Firefox cannot evaluate length÷length in CSS calc().
 */
export function computeGameRouteShellMantineScale(
  shortestSidePx: number,
  windowInnerHeight: number,
): number {
  if (windowInnerHeight <= 649) return 0.8
  return clamp(0.5 + ((shortestSidePx - 400) / 600) * 0.5, 0.5, 1)
}

/** Drop inline overrides so @supports rules in CSS own --mantine-* on supporting engines. */
function clearViewportCssVariableOverrides(): void {
  const html = document.documentElement
  html.style.removeProperty('--mantine-scale')
  html.style.removeProperty('--app-shortest-viewport-side')
  for (const el of document.querySelectorAll('.game-route-shell')) {
    ;(el as HTMLElement).style.removeProperty('--mantine-scale')
  }
}

function applyViewportCssVariableOverrides(): void {
  const { vw, vh, innerH } = readViewportSize()
  const shortest = Math.min(vw, vh)
  const html = document.documentElement

  html.style.setProperty('--app-shortest-viewport-side', `${shortest}px`)
  html.style.setProperty(
    '--mantine-scale',
    String(computeRootMantineScale(vw, vh)),
  )

  const gameScale = computeGameRouteShellMantineScale(shortest, innerH)
  for (const el of document.querySelectorAll('.game-route-shell')) {
    ;(el as HTMLElement).style.setProperty(
      '--mantine-scale',
      String(gameScale),
    )
  }
}

export function syncViewportCssVariables(): void {
  if (typeof document === 'undefined') return

  if (cssTypedLengthRatioSupported()) {
    clearViewportCssVariableOverrides()
  } else {
    applyViewportCssVariableOverrides()
  }
}

/**
 * Firefox / Samsung Internet: sets --mantine-scale (and friends) from JS.
 * Supporting browsers: no inline overrides — CSS @supports formulas track viewport without resize listeners.
 */
export function ViewportCssVariables() {
  const routeKey = useRouterState({ select: s => s.location.pathname })

  useLayoutEffect(() => {
    syncViewportCssVariables()

    if (cssTypedLengthRatioSupported()) {
      return undefined
    }

    const vv = window.visualViewport
    const onResize = () => syncViewportCssVariables()
    window.addEventListener('resize', onResize)
    vv?.addEventListener('resize', onResize)
    vv?.addEventListener('scroll', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
      vv?.removeEventListener('resize', onResize)
      vv?.removeEventListener('scroll', onResize)
    }
  }, [routeKey])

  return null
}
